import { useEffect, useMemo, useState } from "react";
import {
  getWorksheetNames,
  registerWorksheetNamesOnChangeHandler,
  registerWorksheetOnChangeHandler,
  removeEventHandler,
} from "../../utils/excel";
import { Button, Form, FormField, Select, SpaceBetween } from "@amzn/awsui-components-react";
import { OptionDefinition } from "@amzn/awsui-components-react/polaris/internal/components/option/interfaces";
import { ptcToOption, requiredErrorText, worksheetToOption } from "../../utils/polaris";
import { writePtcReplacement } from "../../writers/atinToPtcWriter";
import { parsePtcs, Ptc } from "../../parsers/ptcParser";
import { parseAnalysisSheet } from "../../parsers/analysisParser";
import {
  ANALYSIS_SHEET_CONSTRAINT_TEXT,
  ANALYSIS_WORKSHEET_LABEL,
  DEFAULT_EXCLUDED_PTCS_SHEET_NAME,
  DEFAULT_PTC_TAXABILITY_SHEET_NAME,
  EXCLUDED_PTCS_DESCRIPTION,
  EXCLUDED_PTCS_WORKSHEET_LABEL,
  NEW_PTC_LABEL,
  PTC_REPLACER_INCORRECT_STH_CONFIG_WARNING,
  PTC_REPLACER_USED_BY_NON_STH_ATINS_WARNING,
  PTC_TO_REPLACE_LABEL,
  TDM_CONSTRAINT_TEXT,
  TDM_WORKSHEET_LABEL,
} from "../../utils/constants";
import {
  findPtcsWithBaselineTaxability,
  isPtcValidAlternateOptionForSth,
  isAtinAffectedOrDeprecated,
  getNewPtcName,
} from "../../sth/sth";
import { parseSthExcludedPtcs } from "../../parsers/sthExcludedPtcsParser";
import { caseInsensitiveFind } from "../../utils/stringUtils";

export default function PtcReplacer() {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");

  const [worksheetNames, setWorksheetNames] = useState<string[]>([]);

  const [ptcTdmWorksheet, setPtcTdmWorksheet] = useState<OptionDefinition>();
  const [ptcTdmWorksheetError, setPtcTdmWorksheetError] = useState("");

  const [analysisWorksheet, setAnalysisWorksheet] = useState<OptionDefinition>();
  const [analysisWorksheetError, setAnalysisWorksheetError] = useState("");

  const [excludedPtcsWorksheet, setExcludedPtcsWorksheet] = useState<OptionDefinition>();
  const [excludedPtcsWorksheetError, setExcludedPtcsWorksheetError] = useState("");

  const [ptcToReplace, setPtcToReplace] = useState<OptionDefinition>();
  const [ptcToReplaceError, setPtcToReplaceError] = useState("");

  const [newPtc, setNewPtc] = useState<OptionDefinition>();
  const [newPtcError, setNewPtcError] = useState("");

  const [tdmPtcs, setTdmPtcs] = useState<Map<string, Ptc>>();
  const [allPtcs, setAllPtcs] = useState<Map<string, Ptc>>();

  const [excludedPtcs, setExcludedPtcs] = useState<Set<string>>(new Set());

  const [atinsChangedMessage, setAtinsChangedMessage] = useState("");

  useEffect(() => {
    getWorksheetNames()
      .then((worksheetNames) => {
        setWorksheetNames(worksheetNames);
        setDefaultWorksheetSelection(worksheetNames);
      })
      .catch((err) => setError(err.toString()));

    const worksheetNamesChangeEventHandler = registerWorksheetNamesOnChangeHandler(setWorksheetNames).catch((err) =>
      setError(err.toString())
    );

    // cleanup
    return () => {
      worksheetNamesChangeEventHandler.then((eventHandlers) => {
        if (eventHandlers) {
          eventHandlers.forEach((eventHandler) => removeEventHandler(eventHandler));
        }
      });
    };
  }, []);

  useEffect(() => {
    parsePtcsFromTdmSheet();
    const tdmSheetEventHandler = registerWorksheetOnChangeHandler(parsePtcsFromTdmSheet, ptcTdmWorksheet?.value ?? "");
    // cleanup
    return () => {
      tdmSheetEventHandler.then((eventHandler) => {
        if (eventHandler) {
          removeEventHandler(eventHandler);
        }
      });
    };
  }, [ptcTdmWorksheet?.value]);

  useEffect(() => {
    parsePtcsFromAnalysisSheet();
    const analysisSheetEventHandler = registerWorksheetOnChangeHandler(
      parsePtcsFromAnalysisSheet,
      analysisWorksheet?.value ?? ""
    );
    // cleanup
    return () => {
      analysisSheetEventHandler.then((eventHandler) => {
        if (eventHandler) {
          removeEventHandler(eventHandler);
        }
      });
    };
  }, [analysisWorksheet?.value, tdmPtcs]);

  useEffect(() => {
    parseExcludedPtcsSheet();
    const excludedPtcsSheetEventHandler = registerWorksheetOnChangeHandler(
      parseExcludedPtcsSheet,
      excludedPtcsWorksheet?.value ?? ""
    );
    // cleanup
    return () => {
      excludedPtcsSheetEventHandler.then((eventHandler) => {
        if (eventHandler) {
          removeEventHandler(eventHandler);
        }
      });
    };
  }, [excludedPtcsWorksheet?.value, analysisWorksheet?.value, tdmPtcs]);

  const worksheetOptions: OptionDefinition[] = useMemo(() => worksheetNames.map(worksheetToOption), [worksheetNames]);

  const ptcToReplaceOptions = useMemo(() => {
    return Array.from(allPtcs?.values() ?? [])
      .filter((ptc) => ptc.isUsedForSth)
      .map((ptc) => ptcToOption(ptc));
  }, [allPtcs]);

  const newPtcOptions = useMemo(() => {
    if (!ptcToReplace || !allPtcs) {
      return [];
    }
    const selectedPtc = allPtcs.get(ptcToReplace.value);
    const ptcOptionsWithSameTaxability = findPtcsWithBaselineTaxability(
      selectedPtc.baselineTaxability,
      Array.from(allPtcs.values())
    )
      .filter((ptc) => ptc.ptcName !== ptcToReplace.value && !excludedPtcs.has(ptc.ptcName))
      .sort(sortValidPtcAlternateOptionsFirst)
      .map((ptc) => {
        const numAtinsNotInSth = ptc.atinsMapped.filter((atin) => !isAtinAffectedOrDeprecated(atin)).length;
        if (numAtinsNotInSth > 0) {
          return ptcToOption(ptc, PTC_REPLACER_USED_BY_NON_STH_ATINS_WARNING(numAtinsNotInSth));
        }
        if (ptc.isUsedForSth) {
          return ptcToOption(ptc, PTC_REPLACER_INCORRECT_STH_CONFIG_WARNING);
        }
        return ptcToOption(ptc);
      });

    const newPtcName = getNewPtcName(allPtcs);
    ptcOptionsWithSameTaxability.push({
      label: `Create new PTC (${newPtcName})`,
      value: newPtcName,
    });

    return ptcOptionsWithSameTaxability;
  }, [ptcToReplace, allPtcs]);

  const setDefaultWorksheetSelection = (worksheetNames: string[]) => {
    const defaultPtcTdmWorksheet = caseInsensitiveFind(worksheetNames, DEFAULT_PTC_TAXABILITY_SHEET_NAME);
    if (defaultPtcTdmWorksheet) setPtcTdmWorksheet(worksheetToOption(defaultPtcTdmWorksheet));

    const defaultExcludedPtcsWorksheet = caseInsensitiveFind(worksheetNames, DEFAULT_EXCLUDED_PTCS_SHEET_NAME);
    if (defaultExcludedPtcsWorksheet) setExcludedPtcsWorksheet(worksheetToOption(defaultExcludedPtcsWorksheet));
  };

  const parsePtcsFromTdmSheet = async () => {
    setPtcTdmWorksheetError("");
    setLoading(true);
    setTdmPtcs(undefined);

    try {
      if (!ptcTdmWorksheet?.value) {
        return;
      }

      const tdmPtcs = await parsePtcs(ptcTdmWorksheet.value, setLoadingText);
      setTdmPtcs(tdmPtcs);
    } catch (err) {
      setPtcTdmWorksheetError(err.toString());
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  const parsePtcsFromAnalysisSheet = async () => {
    setAnalysisWorksheetError("");
    setLoading(true);
    setAllPtcs(undefined);

    try {
      if (!analysisWorksheet?.value || !tdmPtcs) {
        return;
      }

      const sthPtcs = await parseAnalysisSheet(analysisWorksheet.value, tdmPtcs, setLoadingText);
      setAllPtcs(sthPtcs);
    } catch (err) {
      setAnalysisWorksheetError(err.toString());
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  const parseExcludedPtcsSheet = async () => {
    setExcludedPtcsWorksheetError("");
    setLoading(true);
    setExcludedPtcs(new Set());

    try {
      if (!excludedPtcsWorksheet?.value || !analysisWorksheet?.value || !tdmPtcs) {
        return;
      }

      const excludedPtcs = await parseSthExcludedPtcs(excludedPtcsWorksheet.value, tdmPtcs, setLoadingText);
      setExcludedPtcs(new Set(excludedPtcs));
    } catch (err) {
      setExcludedPtcsWorksheetError(err.toString());
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  const replacePtc = async () => {
    setError("");
    let hasError = Boolean(ptcTdmWorksheetError || analysisWorksheetError);

    if (!ptcTdmWorksheet) {
      setPtcTdmWorksheetError(requiredErrorText(TDM_WORKSHEET_LABEL));
      hasError = true;
    }
    if (!analysisWorksheet) {
      setAnalysisWorksheetError(requiredErrorText(ANALYSIS_WORKSHEET_LABEL));
      hasError = true;
    }
    if (!ptcToReplace) {
      setPtcToReplaceError(requiredErrorText(PTC_TO_REPLACE_LABEL));
      hasError = true;
    }
    if (!newPtc) {
      setNewPtcError(requiredErrorText(NEW_PTC_LABEL));
      hasError = true;
    }

    if (hasError) return;

    setLoading(true);

    try {
      const isNewPtc = !allPtcs.has(newPtc.value) || allPtcs.get(newPtc.value).isNew;
      const atinsChanged = await writePtcReplacement(
        analysisWorksheet.value,
        ptcToReplace.value,
        newPtc.value,
        isNewPtc,
        setLoadingText
      );
      if (atinsChanged) {
        setAtinsChangedMessage(
          `Success! ${atinsChanged.length} ATIN(s) changed: ${atinsChanged.join(", ")} from ${ptcToReplace.value} to ${
            newPtc.value
          }`
        );
        setPtcToReplace(undefined);
        setNewPtc(undefined);
      }
    } catch (err) {
      setError(err.toString());
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <Form
        errorText={error}
        actions={
          <Button
            onClick={async () => {
              setAtinsChangedMessage("");
              await replacePtc();
            }}
            loading={loading}
            variant="primary"
          >
            Replace PTC
          </Button>
        }
      >
        <SpaceBetween size="m" direction="vertical">
          <FormField label={TDM_WORKSHEET_LABEL} errorText={ptcTdmWorksheetError} constraintText={TDM_CONSTRAINT_TEXT}>
            <Select
              selectedOption={ptcTdmWorksheet}
              onChange={({ detail }) => {
                setPtcTdmWorksheetError("");
                setPtcTdmWorksheet(detail.selectedOption);
              }}
              options={worksheetOptions}
              filteringType="auto"
              disabled={loading}
            />
          </FormField>
          <FormField
            label={ANALYSIS_WORKSHEET_LABEL}
            constraintText={ANALYSIS_SHEET_CONSTRAINT_TEXT}
            errorText={analysisWorksheetError}
          >
            <Select
              selectedOption={analysisWorksheet}
              options={worksheetOptions}
              onChange={({ detail }) => {
                setAnalysisWorksheetError("");
                setAnalysisWorksheet(detail.selectedOption);
              }}
              filteringType="auto"
              disabled={loading}
            />
          </FormField>
          <FormField
            label={EXCLUDED_PTCS_WORKSHEET_LABEL}
            description={EXCLUDED_PTCS_DESCRIPTION}
            errorText={excludedPtcsWorksheetError}
          >
            <Select
              selectedOption={excludedPtcsWorksheet}
              options={worksheetOptions}
              onChange={({ detail }) => {
                setExcludedPtcsWorksheetError("");
                setExcludedPtcsWorksheet(detail.selectedOption);
              }}
              filteringType="auto"
              disabled={loading}
            />
          </FormField>
          <FormField label={PTC_TO_REPLACE_LABEL} errorText={ptcToReplaceError}>
            <Select
              selectedOption={ptcToReplace}
              options={ptcToReplaceOptions}
              onChange={({ detail }) => {
                setPtcToReplaceError("");
                setPtcToReplace(detail.selectedOption);
                setNewPtc(undefined);
              }}
              filteringType="auto"
              disabled={loading}
            />
          </FormField>
          <FormField label={NEW_PTC_LABEL} errorText={newPtcError}>
            <Select
              selectedOption={newPtc}
              options={newPtcOptions}
              onChange={({ detail }) => {
                setNewPtcError("");
                setNewPtc(detail.selectedOption);
              }}
              filteringType="auto"
              disabled={loading}
            />
          </FormField>
        </SpaceBetween>
        <p>
          <small>{loadingText}</small>
        </p>
        <p>{atinsChangedMessage}</p>
      </Form>
    </form>
  );
}

const sortValidPtcAlternateOptionsFirst = (ptc1: Ptc, ptc2: Ptc) => {
  return Number(isPtcValidAlternateOptionForSth(ptc2)) - Number(isPtcValidAlternateOptionForSth(ptc1));
};
